import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DeviceModelStatus, DeviceUnitStatus, GeocodeAccuracy, InstallationStatus, type DeviceUnitComplianceChange, type Installation } from '@regigov/contracts';
import { installationsApi, modelsApi, rejectionsApi, rulesApi, unitsApi } from '../api/devices';
import { useAuth } from '../auth/AuthContext';
import { isCerRole } from '../auth/roles';
import { AttributesList } from './AttributesForm';
import { InstallationForm, ModelForm, TransferForm, UnitForm, type FormMode } from './DeviceForms';
import { AsAtPicker, CorrectionForm, DeviceHistoryPanel, UnitAsAtView, VersionSummary, type CorrectionField } from './DeviceHistory';
import { InstallationsTable, ModelsTable, RejectionsTable, UnitsTable } from './DeviceTables';
import { DefinitionList, Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

type Tab = 'units' | 'models' | 'installations' | 'rejections';
const TABS: Array<[Tab, string]> = [
  ['units', 'Units'],
  ['models', 'Models'],
  ['installations', 'Installations'],
  ['rejections', 'Rejected submissions'],
];

/** Current-state register: search + tabbed lists. CER sees everything; participants see only their own records. */
export function RegisterPage({ mode, basePath }: { mode: FormMode; basePath: string }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'units';
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const deviceClass = params.get('class') ?? '';
  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  const classes = useQuery({ queryKey: ['rules', 'classes'], queryFn: rulesApi.classes });
  const models = useQuery({ queryKey: ['models', { q, status, deviceClass }], queryFn: () => modelsApi.search({ q, status, deviceClass }), enabled: tab === 'models' });
  const units = useQuery({ queryKey: ['units', { q, status, deviceClass }], queryFn: () => unitsApi.search({ q, status, deviceClass }), enabled: tab === 'units' });
  const installations = useQuery({ queryKey: ['installations', { q, status }], queryFn: () => installationsApi.search({ q, status }), enabled: tab === 'installations' });
  const rejections = useQuery({ queryKey: ['rejections'], queryFn: () => rejectionsApi.list(), enabled: tab === 'rejections' });
  const canWrite = mode === 'cer' || (user?.roles.includes('participant_admin') ?? false);
  const statusOptions = tab === 'models' ? DeviceModelStatus.options : tab === 'units' ? DeviceUnitStatus.options : tab === 'installations' ? InstallationStatus.options : [];
  const active = tab === 'models' ? models : tab === 'units' ? units : tab === 'installations' ? installations : rejections;

  return (
    <>
      <div className="row">
        <h1>{mode === 'cer' ? 'Device register' : 'My devices'}</h1>
        {canWrite && (
          <span className="row">
            <Link className="button" to={`${basePath}/models/new`}>
              Register model
            </Link>
            <Link className="button" to={`${basePath}/units/new`}>
              Register unit
            </Link>
            <Link className="button" to={`${basePath}/installations/new`}>
              Record installation
            </Link>
          </span>
        )}
        {mode === 'cer' && <Link to={`${basePath}/rules`}>Rule sets</Link>}
      </div>
      {mode === 'participant' && <p className="notice">Only records owned, manufactured or installed by your organisation are shown. Lists show the current state only.</p>}

      <nav className="tabs" aria-label="Register sections">
        {TABS.map(([t, label]) => (
          <button key={t} type="button" className={t === tab ? 'tab active' : 'tab'} onClick={() => update({ tab: t, status: '' })}>
            {label}
          </button>
        ))}
      </nav>

      {tab !== 'rejections' && (
        <div className="row">
          <label className="grow">
            Search
            <input
              value={q}
              onChange={(e) => update({ q: e.target.value })}
              placeholder={tab === 'installations' ? 'NMI, serial, suburb or postcode' : tab === 'units' ? 'Serial, batch, model or unit ID' : 'Model number, name or model ID'}
            />
          </label>
          {tab !== 'installations' && (
            <label>
              Class
              <select value={deviceClass} onChange={(e) => update({ class: e.target.value })}>
                <option value="">Any</option>
                {(classes.data?.items ?? []).map((c) => (
                  <option key={c.deviceClass} value={c.deviceClass}>
                    {c.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Status
            <select value={status} onChange={(e) => update({ status: e.target.value })}>
              <option value="">Any</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <ProblemAlert error={active.error} />
      {active.isLoading && <p className="muted">Loading…</p>}
      {tab === 'models' && models.data && <ModelsTable items={models.data.items} basePath={basePath} />}
      {tab === 'units' && units.data && <UnitsTable items={units.data.items} basePath={basePath} />}
      {tab === 'installations' && installations.data && <InstallationsTable items={installations.data.items} basePath={basePath} />}
      {tab === 'rejections' && rejections.data && (
        <>
          <p className="muted">Every rejected registration is kept with its structured reasons and the rule version applied.</p>
          <RejectionsTable items={rejections.data.items} showParticipant={mode === 'cer'} />
        </>
      )}
    </>
  );
}

// ---- Registration pages -----------------------------------------------------

export function NewModelPage({ mode, basePath }: { mode: FormMode; basePath: string }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  return (
    <>
      <p className="crumbs">
        <Link to={basePath}>{mode === 'cer' ? 'Device register' : 'My devices'}</Link> / Register model
      </p>
      <h1>Register a device model</h1>
      <ModelForm
        mode={mode}
        onSaved={(m) => {
          void qc.invalidateQueries({ queryKey: ['models'] });
          nav(`${basePath}/models/${m.id}`);
        }}
      />
    </>
  );
}

export function NewUnitPage({ mode, basePath }: { mode: FormMode; basePath: string }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  return (
    <>
      <p className="crumbs">
        <Link to={basePath}>{mode === 'cer' ? 'Device register' : 'My devices'}</Link> / Register unit
      </p>
      <h1>Register a device unit</h1>
      <UnitForm
        mode={mode}
        initialModelId={params.get('modelId') ?? undefined}
        onSaved={(u) => {
          void qc.invalidateQueries({ queryKey: ['units'] });
          void qc.invalidateQueries({ queryKey: ['models'] });
          nav(`${basePath}/units/${u.id}`);
        }}
      />
    </>
  );
}

export function NewInstallationPage({ mode, basePath }: { mode: FormMode; basePath: string }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  return (
    <>
      <p className="crumbs">
        <Link to={basePath}>{mode === 'cer' ? 'Device register' : 'My devices'}</Link> / Record installation
      </p>
      <h1>Record an installation</h1>
      <InstallationForm
        mode={mode}
        initialUnitId={params.get('unitId') ?? undefined}
        onSaved={(i) => {
          void qc.invalidateQueries({ queryKey: ['installations'] });
          void qc.invalidateQueries({ queryKey: ['units'] });
          nav(`${basePath}/installations/${i.id}`);
        }}
      />
    </>
  );
}

// ---- Detail pages -----------------------------------------------------------

const MODEL_FIELDS: CorrectionField[] = [
  { name: 'modelNumber', label: 'Model number', kind: 'text' },
  { name: 'name', label: 'Name', kind: 'text' },
  { name: 'attributes', label: 'Attributes', kind: 'json' },
];
const UNIT_FIELDS: CorrectionField[] = [
  { name: 'serial', label: 'Serial', kind: 'text' },
  { name: 'batch', label: 'Batch', kind: 'text', nullable: true },
  { name: 'ownerParticipantId', label: 'Owner participant ID', kind: 'text' },
  { name: 'attributes', label: 'Attributes', kind: 'json' },
];
const INSTALLATION_FIELDS: CorrectionField[] = [
  { name: 'nmi', label: 'NMI', kind: 'text' },
  { name: 'address', label: 'Address', kind: 'json' },
  { name: 'commissionedOn', label: 'Commissioned on', kind: 'date' },
  { name: 'decommissionedOn', label: 'Decommissioned on', kind: 'date', nullable: true },
  { name: 'attributes', label: 'Attributes', kind: 'json' },
];

function ReasonForm({ title, options, onSubmit, error, pending, extra }: { title: string; options: Array<[string, string]>; onSubmit: (choice: string, reason: string, date: string) => void; error: unknown; pending: boolean; extra?: 'date' }) {
  const [choice, setChoice] = useState(options[0]?.[0] ?? '');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(choice, reason, date);
  };
  return (
    <details className="panel">
      <summary>{title}</summary>
      <form className="form" onSubmit={submit}>
        <div className="grid2">
          {options.length > 1 && (
            <Field label="New status">
              <select value={choice} onChange={(e) => setChoice(e.target.value)}>
                {options.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {extra === 'date' && (
            <Field label="Decommissioned on">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
            </Field>
          )}
        </div>
        <Field label="Reason" hint="Recorded in the audit log">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} rows={2} />
        </Field>
        <ProblemAlert error={error} />
        <div>
          <button type="submit" disabled={pending}>
            Apply
          </button>
        </div>
      </form>
    </details>
  );
}

export function ModelDetailPage({ mode, basePath }: { mode: FormMode; basePath: string }) {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const model = useQuery({ queryKey: ['models', 'detail', id], queryFn: () => modelsApi.get(id) });
  const units = useQuery({ queryKey: ['units', { modelId: id }], queryFn: () => unitsApi.search({ modelId: id }) });
  const change = useMutation({
    mutationFn: (v: { status: 'approved' | 'withdrawn'; changeReason: string }) => modelsApi.setStatus(id, v),
    onSuccess: (m) => {
      qc.setQueryData(['models', 'detail', id], m);
      void qc.invalidateQueries({ queryKey: ['models'] });
    },
  });
  if (model.isLoading) return <p className="muted">Loading…</p>;
  if (model.error || !model.data) return <ProblemAlert error={model.error ?? new Error('Not found')} />;
  const m = model.data;
  return (
    <>
      <p className="crumbs">
        <Link to={`${basePath}?tab=models`}>Models</Link> / {m.modelNumber}
      </p>
      <div className="row">
        <h1>
          {m.modelNumber} <span className="muted">{m.name}</span>
        </h1>
        <StatusBadge status={m.status} />
      </div>
      <section className="card">
        <DefinitionList
          items={[
            ['Model ID', <code>{m.id}</code>],
            ['Device class', m.deviceClass],
            ['Manufacturer', mode === 'cer' ? <Link to={`/cer/participants/${m.manufacturerId}`}>{m.manufacturerName}</Link> : m.manufacturerName],
            ['Effective date', m.effectiveDate],
            ['Rules applied', `${m.deviceClass} v${m.ruleVersionApplied}`],
            ['Registered units', m.unitCount],
            ['Last updated', formatDateTime(m.updatedAt)],
          ]}
        />
        <h3>Attributes</h3>
        <AttributesList attributes={m.attributes} />
      </section>
      {mode === 'cer' && m.status !== 'withdrawn' && (
        <ReasonForm
          title="Change model status"
          options={m.status === 'pending' ? [['approved', 'Approve'], ['withdrawn', 'Withdraw']] : [['withdrawn', 'Withdraw']]}
          onSubmit={(status, changeReason) => change.mutate({ status: status as 'approved' | 'withdrawn', changeReason })}
          error={change.error}
          pending={change.isPending}
        />
      )}
      {mode === 'participant' && m.status === 'pending' && <p className="notice">Awaiting CER approval. Units cannot be registered against a pending model.</p>}
      <div className="row">
        <h2>Units</h2>
        {m.status === 'approved' && <Link to={`${basePath}/units/new?modelId=${m.id}`}>Register unit of this model</Link>}
      </div>
      <ProblemAlert error={units.error} />
      {units.data && <UnitsTable items={units.data.items} basePath={basePath} />}
      <DeviceHistoryPanel queryKey={['models', 'history', id]} load={() => modelsApi.history(id)}>
        <AsAtPicker queryKey={['models', id]} load={(q) => modelsApi.at(id, q)} render={(v) => <VersionSummary title="Model" version={v} />} />
        {mode === 'cer' && (
          <CorrectionForm
            queryKey={['models', 'history', id]}
            load={() => modelsApi.history(id)}
            fields={MODEL_FIELDS}
            submit={(body) => modelsApi.correct(id, body)}
            onCorrected={(next) => {
              qc.setQueryData(['models', 'detail', id], next);
              void qc.invalidateQueries({ queryKey: ['models'] });
            }}
          />
        )}
      </DeviceHistoryPanel>
    </>
  );
}

const COMPLIANCE_OPTIONS: Array<[DeviceUnitComplianceChange['complianceStatus'], string]> = [
  ['compliant', 'Compliant'],
  ['under_review', 'Under review'],
  ['non_compliant', 'Non-compliant'],
];

export function UnitDetailPage({ mode, basePath }: { mode: FormMode; basePath: string }) {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const unit = useQuery({ queryKey: ['units', 'detail', id], queryFn: () => unitsApi.get(id) });
  const installations = useQuery({ queryKey: ['installations', { unitId: id }], queryFn: () => installationsApi.search({ unitId: id }) });
  const change = useMutation({
    mutationFn: (v: { status: 'decommissioned' | 'withdrawn'; changeReason: string }) => unitsApi.setStatus(id, v),
    onSuccess: (u) => {
      qc.setQueryData(['units', 'detail', id], u);
      void qc.invalidateQueries({ queryKey: ['units'] });
      void qc.invalidateQueries({ queryKey: ['installations'] });
    },
  });
  const compliance = useMutation({
    mutationFn: (v: DeviceUnitComplianceChange) => unitsApi.setCompliance(id, v),
    onSuccess: (u) => {
      qc.setQueryData(['units', 'detail', id], u);
      void qc.invalidateQueries({ queryKey: ['units'] });
    },
  });
  if (unit.isLoading) return <p className="muted">Loading…</p>;
  if (unit.error || !unit.data) return <ProblemAlert error={unit.error ?? new Error('Not found')} />;
  const u = unit.data;
  const canTransfer = (u.status === 'registered' || u.status === 'installed') && (mode === 'cer' || (user?.roles.includes('participant_admin') && user.participantId === u.ownerParticipantId));
  const afterUnitChange = (next: typeof u) => {
    qc.setQueryData(['units', 'detail', id], next);
    void qc.invalidateQueries({ queryKey: ['units'] });
    void qc.invalidateQueries({ queryKey: ['installations'] });
  };
  return (
    <>
      <p className="crumbs">
        <Link to={`${basePath}?tab=units`}>Units</Link> / {u.serial}
      </p>
      <div className="row">
        <h1>Serial {u.serial}</h1>
        <StatusBadge status={u.status} />
        <StatusBadge status={u.complianceStatus} />
      </div>
      <section className="card">
        <DefinitionList
          items={[
            ['Unit ID', <code>{u.id}</code>],
            ['Model', <Link to={`${basePath}/models/${u.modelId}`}>{u.modelNumber} — {u.modelName}</Link>],
            ['Device class', u.deviceClass],
            ['Manufacturer', u.manufacturerName],
            ['Owner', mode === 'cer' ? <Link to={`/cer/participants/${u.ownerParticipantId}`}>{u.ownerName}</Link> : u.ownerName],
            ['Batch', u.batch],
            ['Compliance status', <StatusBadge status={u.complianceStatus} />],
            ['Effective date', u.effectiveDate],
            ['Rules applied', `${u.deviceClass} v${u.ruleVersionApplied}`],
            ['Active installation', u.activeInstallationId ? <Link to={`${basePath}/installations/${u.activeInstallationId}`}>{u.activeInstallationId}</Link> : null],
            ['Last updated', formatDateTime(u.updatedAt)],
          ]}
        />
        <h3>Attributes</h3>
        <AttributesList attributes={u.attributes} />
      </section>
      {mode === 'cer' && (u.status === 'registered' || u.status === 'installed') && (
        <ReasonForm
          title="Change unit status"
          options={[['decommissioned', 'Decommission'], ['withdrawn', 'Withdraw']]}
          onSubmit={(status, changeReason) => change.mutate({ status: status as 'decommissioned' | 'withdrawn', changeReason })}
          error={change.error}
          pending={change.isPending}
        />
      )}
      {mode === 'cer' && isCerRole(user?.roles) && (
        <ReasonForm
          key={u.complianceStatus}
          title="Set compliance status"
          options={COMPLIANCE_OPTIONS.filter(([v]) => v !== u.complianceStatus)}
          onSubmit={(complianceStatus, changeReason) => compliance.mutate({ complianceStatus: complianceStatus as DeviceUnitComplianceChange['complianceStatus'], changeReason })}
          error={compliance.error}
          pending={compliance.isPending}
        />
      )}
      <div className="row">
        <h2>Installations</h2>
        {u.status === 'registered' && <Link to={`${basePath}/installations/new?unitId=${u.id}`}>Record installation of this unit</Link>}
      </div>
      <ProblemAlert error={installations.error} />
      {installations.data && <InstallationsTable items={installations.data.items} basePath={basePath} />}
      {canTransfer && <TransferForm unit={u} mode={mode} onTransferred={afterUnitChange} />}
      <DeviceHistoryPanel queryKey={['units', 'history', id]} load={() => unitsApi.history(id)} compliance>
        <AsAtPicker queryKey={['units', id]} load={(q) => unitsApi.at(id, q)} render={(r) => <UnitAsAtView result={r} />} />
        {mode === 'cer' && <CorrectionForm queryKey={['units', 'history', id]} load={() => unitsApi.history(id)} fields={UNIT_FIELDS} submit={(body) => unitsApi.correct(id, body)} onCorrected={afterUnitChange} />}
      </DeviceHistoryPanel>
    </>
  );
}

export function locationText(i: Installation): string {
  if (i.geocodeStatus === 'not_recorded') return 'Not recorded';
  if (!i.location) return 'Not geocoded';
  return `${i.location.lat.toFixed(5)}, ${i.location.lon.toFixed(5)} (${i.location.source}, ${i.location.accuracy})`;
}

/** CER follow-up: an officer sets the point; it's saved as a correction and the address joins the register as manual. */
function SetLocationForm({ installation, onSaved }: { installation: Installation; onSaved: (i: Installation) => void }) {
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [accuracy, setAccuracy] = useState<GeocodeAccuracy>('rooftop');
  const [changeReason, setReason] = useState('');
  const save = useMutation({
    mutationFn: async () => {
      const latest = (await installationsApi.history(installation.id)).versions.find((v) => v.supersededBy === null && v.validTo === null);
      if (!latest) throw new Error('No current version to correct');
      return installationsApi.correct(installation.id, { correctionOf: latest.versionId, changeReason, changes: { location: { lat: Number(lat), lon: Number(lon), accuracy } } });
    },
    onSuccess: onSaved,
  });
  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <h2>Set location</h2>
      <p className="muted">This address isn't in the known-address register. The point you enter is saved as a correction (a new version), and the address is added to the register as manual.</p>
      <div className="grid2">
        <Field label="Latitude" hint="-55 to -9">
          <input type="number" step="any" min={-55} max={-9} value={lat} onChange={(e) => setLat(e.target.value)} required />
        </Field>
        <Field label="Longitude" hint="112 to 160">
          <input type="number" step="any" min={112} max={160} value={lon} onChange={(e) => setLon(e.target.value)} required />
        </Field>
        <Field label="Accuracy">
          <select value={accuracy} onChange={(e) => setAccuracy(e.target.value as GeocodeAccuracy)}>
            {GeocodeAccuracy.options.map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </Field>
        <Field label="Reason">
          <input value={changeReason} onChange={(e) => setReason(e.target.value)} required minLength={3} />
        </Field>
      </div>
      <ProblemAlert error={save.error} />
      <button type="submit" disabled={save.isPending}>
        Save location
      </button>
    </form>
  );
}

const QUEUE_LIMIT = 200;

/** CER follow-up queue: active installations whose address wasn't in the register. */
export function NotGeocodedPage({ basePath }: { basePath: string }) {
  const list = useQuery({ queryKey: ['installations', { status: 'active', geocodeStatus: 'not_geocoded' }], queryFn: () => installationsApi.search({ status: 'active', geocodeStatus: 'not_geocoded', limit: QUEUE_LIMIT }) });
  return (
    <>
      <p className="crumbs">
        <Link to={`${basePath}?tab=installations`}>Installations</Link> / Not geocoded
      </p>
      <h1>Not geocoded</h1>
      <p className="muted">Active installations whose address wasn't in the known-address register. Open one to set its location.</p>
      <ProblemAlert error={list.error} />
      {list.data?.items.length === QUEUE_LIMIT && <p className="muted">Showing the {QUEUE_LIMIT} most recently commissioned. More appear as you set locations.</p>}
      {list.data && (list.data.items.length ? <InstallationsTable items={list.data.items} basePath={basePath} /> : <p className="muted">None.</p>)}
    </>
  );
}

export function InstallationDetailPage({ mode, basePath }: { mode: FormMode; basePath: string }) {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const inst = useQuery({ queryKey: ['installations', 'detail', id], queryFn: () => installationsApi.get(id) });
  const decommission = useMutation({
    mutationFn: (v: { decommissionedOn: string; changeReason: string }) => installationsApi.decommission(id, v),
    onSuccess: (i) => {
      qc.setQueryData(['installations', 'detail', id], i);
      void qc.invalidateQueries({ queryKey: ['installations'] });
      void qc.invalidateQueries({ queryKey: ['units'] });
    },
  });
  if (inst.isLoading) return <p className="muted">Loading…</p>;
  if (inst.error || !inst.data) return <ProblemAlert error={inst.error ?? new Error('Not found')} />;
  const i = inst.data;
  const canDecommission = i.status === 'active' && (mode === 'cer' || (user?.roles.includes('participant_admin') && user.participantId === i.installerParticipantId));
  return (
    <>
      <p className="crumbs">
        <Link to={`${basePath}?tab=installations`}>Installations</Link> / {i.nmi}
      </p>
      <div className="row">
        <h1>NMI {i.nmi}</h1>
        <StatusBadge status={i.status} />
      </div>
      <section className="card">
        <DefinitionList
          items={[
            ['Installation ID', <code>{i.id}</code>],
            ['Unit', <Link to={`${basePath}/units/${i.unitId}`}>{i.serial} — {i.modelNumber}</Link>],
            ['Device class', i.deviceClass],
            ['Installer', mode === 'cer' ? <Link to={`/cer/participants/${i.installerParticipantId}`}>{i.installerName}</Link> : i.installerName],
            ['Site', `${i.address.line1}${i.address.line2 ? `, ${i.address.line2}` : ''}, ${i.address.suburb} ${i.address.state} ${i.address.postcode}`],
            ['Location', locationText(i)],
            ['Commissioned on', i.commissionedOn],
            ['Decommissioned on', i.decommissionedOn],
            ['Rules applied', `${i.deviceClass} v${i.ruleVersionApplied}`],
            ['Last updated', formatDateTime(i.updatedAt)],
          ]}
        />
        <h3>Attributes</h3>
        <AttributesList attributes={i.attributes} />
      </section>
      {mode === 'cer' && i.geocodeStatus === 'not_geocoded' && <SetLocationForm installation={i} onSaved={(next) => {
        qc.setQueryData(['installations', 'detail', id], next);
        void qc.invalidateQueries({ queryKey: ['installations'] });
      }} />}
      {canDecommission && (
        <ReasonForm
          title="Decommission this installation"
          options={[['decommissioned', 'Decommission']]}
          extra="date"
          onSubmit={(_c, changeReason, decommissionedOn) => decommission.mutate({ decommissionedOn, changeReason })}
          error={decommission.error}
          pending={decommission.isPending}
        />
      )}
      <DeviceHistoryPanel queryKey={['installations', 'history', id]} load={() => installationsApi.history(id)}>
        <AsAtPicker queryKey={['installations', id]} load={(q) => installationsApi.at(id, q)} render={(v) => <VersionSummary title="Installation" version={v} />} />
        {mode === 'cer' && (
          <CorrectionForm
            queryKey={['installations', 'history', id]}
            load={() => installationsApi.history(id)}
            fields={INSTALLATION_FIELDS}
            submit={(body) => installationsApi.correct(id, body)}
            onCorrected={(next) => {
              qc.setQueryData(['installations', 'detail', id], next);
              void qc.invalidateQueries({ queryKey: ['installations'] });
              void qc.invalidateQueries({ queryKey: ['units'] });
            }}
          />
        )}
      </DeviceHistoryPanel>
    </>
  );
}
